import React, { useState, useEffect } from 'react';
import { Button, message, Spin, Table, Modal, Radio, Select, Card, Divider, Row, Col, Typography, Space, Progress } from 'antd';
import NavbarMex from '../components/navbarMex';
import { useParams } from 'react-router-dom';
import BotonWhatsapp from '../botonWhatsapp/botonWhatsapp';
import * as XLSX from 'xlsx';
import { descargarPlantilla } from './plantillaGenerator';
import './EnvioMasivo.css';

const { Option } = Select;
const { Title, Text } = Typography;

const EnvioMasivo = () => {
  const { codigoSucursal: paramCodigoSucursal } = useParams(); 
  const [archivo, setArchivo] = useState(null);
  const [cotizaciones, setCotizaciones] = useState(null);
  const [worksheet, setWorksheet] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [archivoNombre, setArchivoNombre] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [cotizacionModalVisible, setCotizacionModalVisible] = useState(false);
  const [transportadoraSeleccionada, setTransportadoraSeleccionada] = useState('interrapidisimo');
  const [preenviosGenerados, setPreenviosGenerados] = useState(null);
  const [paso, setPaso] = useState(1); // 1: Upload, 2: Cotizar, 3: Preenvío, 4: PDF

  // Obtener el código de sucursal del localStorage o de los parámetros
  const getCodigoSucursal = () => {
    return localStorage.getItem('codigoSucursal') || paramCodigoSucursal;
  };

  useEffect(() => {
    const codigoSucursal = paramCodigoSucursal || localStorage.getItem('codigoSucursal');
    if (codigoSucursal) {
      localStorage.setItem('codigoSucursal', codigoSucursal);
    }
  }, [paramCodigoSucursal]);

  // Función para procesar el archivo Excel y extraer los datos
  const procesarExcel = (worksheet) => {
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    const headers = jsonData[0];
    const rows = jsonData.slice(1);
    
    return rows.map((row, index) => {
      const obj = {};
      headers.forEach((header, i) => {
        obj[header] = row[i] || '';
      });
      return {
        id: String(index + 1).padStart(4, '0'),
        ...obj
      };
    });
  };

  // Función para convertir datos de Excel al formato de cotización
  const convertirAFormatoCotizacion = (data) => {
    return data.map(item => ({
      id: item.id,
      destino: {
        nombre: "",
        codigo: item.codigoPostal || item.codigo_postal || ""
      },
      IdTipoEntrega: parseInt(item.IdTipoEntrega) || 1,
      IdServicio: parseInt(item.IdServicio) || 2,
      valorDeclarado: parseFloat(item.valorDeclarado) || 50000,
      peso: parseFloat(item.peso) || 1,
      alto: parseFloat(item.alto) || 10,
      largo: parseFloat(item.largo) || 10,
      ancho: parseFloat(item.ancho) || 10,
      fecha: item.fecha || "03-04-2025",
      AplicaContrapago: item.AplicaContrapago === 'true' || item.AplicaContrapago === true,
      seguro99: item.seguro99 === 'true' || item.seguro99 === true
    }));
  };

  // Función para convertir datos a formato de preenvío
  const convertirAFormatoPreenvio = (data) => {
    return data.map(item => ({
      id: item.id,
      IdTipoEntrega: parseInt(item.IdTipoEntrega) || 1,
      IdServicio: parseInt(item.IdServicio) || 1,
      AplicaContrapago: item.AplicaContrapago === 'true' || item.AplicaContrapago === true,
      peso: parseFloat(item.peso) || 1,
      largo: parseFloat(item.largo) || 10,
      ancho: parseFloat(item.ancho) || 10,
      alto: parseFloat(item.alto) || 10,
      diceContener: item.diceContener || "",
      valorDeclarado: parseFloat(item.valorDeclarado) || 50000,
      Destinatario: {
        tipoDocumento: item.tipoDocumento || "CC",
        numeroDocumento: item.numeroDocumento || "",
        nombre: item.nombre || "",
        primerApellido: item.primerApellido || "",
        segundoApellido: item.segundoApellido || "",
        telefono: item.telefono || "",
        direccion: item.direccion || "",
        idLocalidad: item.idLocalidad || "",
        correo: item.correo || ""
      },
      Observaciones: item.Observaciones || "",
      origenCreacion: 1,
      seguro99: item.seguro99 === 'true' || item.seguro99 === true
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (file && allowedTypes.includes(file.type)) {
      setArchivo(file);
      setArchivoNombre(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const ws = workbook.Sheets[sheetName];
        setWorksheet(ws);
        
        // Procesar los datos del Excel
        const processedData = procesarExcel(ws);
        setExcelData(processedData);
      };
      reader.readAsArrayBuffer(file);
    } else {
      message.error('Por favor seleccione un archivo Excel válido.');
    }
  };

  // Función para cotizar masivamente
  const cotizarMasivo = async () => {
    if (!excelData.length) {
      message.error('No hay datos para cotizar.');
      return;
    }

    const codigoSucursal = getCodigoSucursal();
    if (!codigoSucursal) {
      message.error('No se encontró el código de sucursal.');
      return;
    }

    try {
      setLoading(true);
      const cotizacionesData = convertirAFormatoCotizacion(excelData);
      const token = localStorage.getItem('token');
      
      const requestBody = {
        transportadora: {
          pais: "colombia",
          nombre: transportadoraSeleccionada
        },
        cotizaciones: cotizacionesData
      };

      const response = await fetch(`https://integration.99envios.app/api/sucursal/cotizar_masivo/${codigoSucursal}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Error al realizar las cotizaciones');
      }

      const data = await response.json();
      setCotizaciones(data);
      setPaso(2);
      message.success(`Cotizaciones procesadas: ${data.cotizaciones_exitosas}/${data.total_cotizaciones}`);
      
    } catch (error) {
      message.error(`Error al cotizar: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Función para crear preenvíos masivos
  const crearPreenviosMasivos = async () => {
    if (!excelData.length) {
      message.error('No hay datos para crear preenvíos.');
      return;
    }

    const codigoSucursal = getCodigoSucursal();
    if (!codigoSucursal) {
      message.error('No se encontró el código de sucursal.');
      return;
    }

    try {
      setLoading(true);
      const preenviosData = convertirAFormatoPreenvio(excelData);
      const token = localStorage.getItem('token');
      
      const requestBody = {
        transportadora: {
          pais: "colombia",
          nombre: transportadoraSeleccionada
        },
        preenvios: preenviosData
      };

      const response = await fetch(`https://integration.99envios.app/api/sucursal/preenvio_masivo/${codigoSucursal}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Error al crear los preenvíos');
      }

      const data = await response.json();
      setPreenviosGenerados(data);
      setPaso(3);
      message.success(`Preenvíos creados: ${data.guias_exitosas}/${data.total_guias}`);
      
    } catch (error) {
      message.error(`Error al crear preenvíos: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Función para generar PDFs masivos
  const generarPDFsMasivos = async () => {
    if (!preenviosGenerados || !preenviosGenerados.resultados) {
      message.error('No hay preenvíos para generar PDFs.');
      return;
    }

    const codigoSucursal = getCodigoSucursal();
    if (!codigoSucursal) {
      message.error('No se encontró el código de sucursal.');
      return;
    }

    try {
      setLoading(true);
      
      // Extraer los números de preenvío
      const guias = Object.values(preenviosGenerados.resultados)
        .filter(resultado => resultado.exito)
        .map(resultado => resultado.numero_preenvio);

      const token = localStorage.getItem('token');

      const requestBody = {
        guias: guias,
        formato: 2,
        transportadora: {
          pais: "colombia",
          nombre: transportadoraSeleccionada
        }
      };

      const response = await fetch(`https://integration.99envios.app/api/sucursal/pdf_masivo/${codigoSucursal}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Error al generar los PDFs');
      }

      const data = await response.json();
      setPaso(4);
      message.success(`PDFs generados: ${data.pdfs_exitosos}/${data.total_guias}`);
      
      // Abrir los PDFs exitosos en nuevas pestañas
      Object.values(data.resultados).forEach(resultado => {
        if (resultado.exito && resultado.url_pdf) {
          window.open(resultado.url_pdf, '_blank');
        }
      });
      
    } catch (error) {
      message.error(`Error al generar PDFs: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NavbarMex title="Envío Masivo" />
      <Spin spinning={loading} className="spinner-container-masivo_beta">
        <div className="container-masivo_beta">
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Title level={2} className="title-masivo_beta">
                Sistema de Envío Masivo
              </Title>
            </Col>
          </Row>

          {/* Paso 1 centrado cuando no hay archivo cargado */}
          {excelData.length === 0 && (
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <div className="step1-container-masivo_beta">
                  <Card 
                    title="📁 Paso 1: Cargar Archivo Excel" 
                    className="card-masivo_beta step1-card-masivo_beta"
                    size="small"
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <input 
                        type="file" 
                        accept=".xlsx, .xls" 
                        onChange={handleFileChange} 
                        className="file-input-masivo_beta"
                      />
                      
                      <Button
                        type="primary"
                        onClick={descargarPlantilla}
                        className="download-button-masivo_beta"
                        size="large"
                      >
                        📥 Descargar Plantilla
                      </Button>
                    </Space>
                  </Card>
                </div>
              </Col>
            </Row>
          )}

          {/* Layout de dos columnas cuando hay archivo cargado */}
          {excelData.length > 0 && (
            <Row gutter={[24, 24]}>
              {/* Columna izquierda - Configuración */}
              <Col xs={24} lg={8}>
                {/* Paso 1: Cargar archivo */}
                <Card 
                  title="📁 Paso 1: Cargar Archivo Excel" 
                  className="card-masivo_beta config-card-masivo_beta"
                  size="small"
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <input 
                      type="file" 
                      accept=".xlsx, .xls" 
                      onChange={handleFileChange} 
                      className="file-input-masivo_beta"
                    />
                    
                    <Button
                      type="primary"
                      onClick={descargarPlantilla}
                      className="download-button-masivo_beta"
                      size="large"
                    >
                      📥 Descargar Plantilla
                    </Button>
                    
                    {archivo && (
                      <div className="file-selected-masivo_beta">
                        <Text strong>📄 {archivoNombre}</Text>
                        <br />
                        <Text type="success">✅ {excelData.length} registros encontrados</Text>
                      </div>
                    )}
                  </Space>
                </Card>

                {/* Selección de transportadora */}
                <Card 
                  title="🚚 Paso 2: Transportadora" 
                  className="card-masivo_beta config-card-masivo_beta"
                  size="small"
                >
                  <Select
                    className="transportadora-select-masivo_beta"
                    placeholder="Seleccionar transportadora"
                    onChange={(value) => setTransportadoraSeleccionada(value)}
                    value={transportadoraSeleccionada}
                    size="large"
                  >
                    <Option value="interrapidisimo">🚛 Interrapidisimo</Option>
                    <Option value="tcc">🚐 TCC</Option>
                    <Option value="servientrega">📦 Servientrega</Option>
                    <Option value="coordinadora">🚚 Coordinadora</Option>
                  </Select>
                </Card>

                {/* Acciones principales */}
                {transportadoraSeleccionada && (
                  <Card 
                    title="⚡ Acciones" 
                    className="card-masivo_beta config-card-masivo_beta"
                    size="small"
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Button 
                        onClick={cotizarMasivo} 
                        disabled={loading}
                        className="action-button-masivo_beta cotizar-button-masivo_beta"
                      >
                        💰 Cotizar Masivo
                      </Button>
                      
                      {cotizaciones && cotizaciones.cotizaciones_exitosas > 0 && (
                        <Button 
                          onClick={crearPreenviosMasivos} 
                          disabled={loading}
                          className="action-button-masivo_beta preenvio-button-masivo_beta"
                        >
                          📋 Crear Preenvíos Masivos
                        </Button>
                      )}
                      
                      {preenviosGenerados && preenviosGenerados.guias_exitosas > 0 && (
                        <Button 
                          onClick={generarPDFsMasivos} 
                          disabled={loading}
                          className="action-button-masivo_beta pdf-button-masivo_beta"
                        >
                          📄 Generar PDFs Masivos
                        </Button>
                      )}
                    </Space>
                  </Card>
                )}
              </Col>

              {/* Columna derecha - Resultados */}
              <Col xs={24} lg={16}>
                {/* Progreso */}
                <Card className="card-masivo_beta progress-card-masivo_beta" size="small">
                  <Progress 
                    percent={
                      !cotizaciones ? 25 : 
                      !preenviosGenerados ? 50 : 
                      paso === 4 ? 100 : 75
                    }
                    status={loading ? "active" : "normal"}
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                  />
                  <Text className="progress-text-masivo_beta">
                    {
                      !cotizaciones ? "📋 Archivo cargado - Listo para cotizar" :
                      !preenviosGenerados ? "💰 Cotizaciones completadas - Listo para preenvíos" :
                      paso === 4 ? "✅ Proceso completado" : "📋 Preenvíos creados - Listo para PDFs"
                    }
                  </Text>
                </Card>

                {/* Resultados de Cotizaciones */}
                {cotizaciones && (
                  <Card 
                    title={`💰 Resultados de Cotizaciones - ${transportadoraSeleccionada.toUpperCase()}`} 
                    className="card-masivo_beta"
                    size="small"
                  >
                    <Row gutter={[16, 16]} className="stats-row-masivo_beta">
                      <Col span={8}>
                        <div className="stat-card-total-masivo_beta">
                          <Text className="stat-label-masivo_beta">Total</Text>
                          <div className="stat-number-total-masivo_beta">
                            {cotizaciones.total_cotizaciones}
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="stat-card-success-masivo_beta">
                          <Text className="stat-label-masivo_beta">Exitosas</Text>
                          <div className="stat-number-success-masivo_beta">
                            {cotizaciones.cotizaciones_exitosas}
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="stat-card-error-masivo_beta">
                          <Text className="stat-label-masivo_beta">Fallidas</Text>
                          <div className="stat-number-error-masivo_beta">
                            {cotizaciones.cotizaciones_fallidas}
                          </div>
                        </div>
                      </Col>
                    </Row>
                    
                    {cotizaciones.cotizaciones_exitosas > 0 && (
                      <Table
                        className="table-masivo_beta"
                        dataSource={Object.entries(cotizaciones.resultados).map(([key, value]) => ({
                          key,
                          id: key,
                          ...value
                        }))}
                        columns={[
                          { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
                          { title: 'Valor', dataIndex: 'valor', key: 'valor', render: (val) => `$${val?.toLocaleString()}`, width: 120 },
                          { title: 'Días', dataIndex: 'dias', key: 'dias', width: 80 },
                          { title: 'Contrapago', dataIndex: 'valor_contrapago', key: 'valor_contrapago', render: (val) => `$${val?.toLocaleString()}`, width: 120 },
                          { title: 'Seguro 99', dataIndex: 'seguro99', key: 'seguro99', render: (val) => `$${val?.toLocaleString()}`, width: 120 },
                          { title: 'Estado', dataIndex: 'exito', key: 'exito', render: (val) => val ? '✅ Exitoso' : '❌ Fallido', width: 100 }
                        ]}
                        pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true }}
                        scroll={{ y: 400, x: '100%' }}
                        size="small"
                      />
                    )}
                  </Card>
                )}

                {/* Resultados de Preenvíos */}
                {preenviosGenerados && (
                  <Card 
                    title={`📋 Resultados de Preenvíos - ${transportadoraSeleccionada.toUpperCase()}`} 
                    className="card-masivo_beta"
                    size="small"
                  >
                    <Row gutter={[16, 16]} className="stats-row-masivo_beta">
                      <Col span={8}>
                        <div className="stat-card-total-masivo_beta">
                          <Text className="stat-label-masivo_beta">Total Guías</Text>
                          <div className="stat-number-total-masivo_beta">
                            {preenviosGenerados.total_guias}
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="stat-card-success-masivo_beta">
                          <Text className="stat-label-masivo_beta">Exitosas</Text>
                          <div className="stat-number-success-masivo_beta">
                            {preenviosGenerados.guias_exitosas}
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="stat-card-error-masivo_beta">
                          <Text className="stat-label-masivo_beta">Fallidas</Text>
                          <div className="stat-number-error-masivo_beta">
                            {preenviosGenerados.guias_fallidas}
                          </div>
                        </div>
                      </Col>
                    </Row>
                    
                    {preenviosGenerados.guias_exitosas > 0 && (
                      <>
                        <Table
                          className="table-masivo_beta"
                          dataSource={Object.entries(preenviosGenerados.resultados).map(([key, value]) => ({
                            key,
                            id: key,
                            ...value
                          }))}
                          columns={[
                            { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
                            { title: 'Número Preenvío', dataIndex: 'numero_preenvio', key: 'numero_preenvio', width: 140 },
                            { title: 'Valor Flete', dataIndex: 'valor_flete', key: 'valor_flete', render: (val) => `$${val?.toLocaleString()}`, width: 120 },
                            { title: 'Sobreflete', dataIndex: 'valorSobreFlete', key: 'valorSobreFlete', render: (val) => `$${val?.toLocaleString()}`, width: 120 },
                            { title: 'Contrapago', dataIndex: 'valorServicioContraPago', key: 'valorServicioContraPago', render: (val) => `$${val?.toLocaleString()}`, width: 120 },
                            { title: 'Estado', dataIndex: 'exito', key: 'exito', render: (val) => val ? '✅ Exitoso' : '❌ Fallido', width: 100 }
                          ]}
                          pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true }}
                          scroll={{ y: 400, x: '100%' }}
                          size="small"
                        />
                        
                        <div className="info-container-masivo_beta">
                          <Text className="info-text-masivo_beta">
                            ℹ️ Los PDFs se abrirán automáticamente en nuevas pestañas una vez generados
                          </Text>
                        </div>
                      </>
                    )}
                  </Card>
                )}
              </Col>
            </Row>
          )}
        </div>
      </Spin>
      <BotonWhatsapp />
    </>
  );
};

export default EnvioMasivo;
